import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faComments,
  faHistory,
  faUserMd,
  faCheckCircle,
  faArchive,
  faSearch,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import { Pagination, CircularProgress } from "@mui/material";
import "react-chat-elements/dist/main.css";
import Notification from "../../Components/Core/Notification";
import Loader from "../../Components/Core/Loader";
import {
  GetDoctorConsultations,
  GetDoctorConsultationsHistory,
} from "../../Services/ConsultionService";
import ChatItem from "../../Components/Consultation/ChatItem";

const DoctorChatIndex = () => {
  const [activeSessions, setActiveSessions] = useState([]);
  const [historySessions, setHistorySessions] = useState([]);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const [initialLoading, setInitialLoading] = useState(true);
  const [listLoading, setListLoading] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();
  const type = location.state?.status || "active";

  // Pagination & Filter States
  const [search, setSearch] = useState(""); // The actual search term sent to API
  const [keyword, setKeyword] = useState(""); // The text currently being typed
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 10;

  useEffect(() => {
    document.title = "لیست مشاوره های پزشک";
    loadConsultations(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Trigger loadConsultations when 'search' (submitted), 'page', or 'type' changes
  useEffect(() => {
    if (!initialLoading) {
      loadConsultations(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, page, search]);

  const loadConsultations = async (isInitial = false) => {
    try {
      if (isInitial) {
        setInitialLoading(true);
      } else {
        setListLoading(true);
      }

      const filterParams = {
        page,
        pageSize,
        search, // Uses the submitted search term
      };

      if (type === "active") {
        const activeRes = await GetDoctorConsultations(filterParams);
        if (activeRes.status !== 204) {
          const data = await activeRes.json();
          setActiveSessions(data.items || []);
          setTotalPages(data.totalPages || 1);
          setTotalCount(data.totalCount || 0);
        } else {
          setActiveSessions([]);
          setTotalPages(1);
          setTotalCount(0);
        }
      } else if (type === "finished") {
        const historyRes = await GetDoctorConsultationsHistory(filterParams);
        if (historyRes.status !== 204) {
          const data = await historyRes.json();
          setHistorySessions(data.items || []);
          setTotalPages(data.totalPages || 1);
          setTotalCount(data.totalCount || 0);
        } else {
          setHistorySessions([]);
          setTotalPages(1);
          setTotalCount(0);
        }
      }
    } catch (error) {
      setSnackbarMessage(error.message);
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
    } finally {
      setInitialLoading(false);
      setListLoading(false);
    }
  };

  const handleJoinChat = (sessionId) => {
    navigate(`/counseling/chat/${sessionId}`, {
      state: { from: "/counseling/doctorChats", status: type },
    });
  };

  // Updates only the input field, does NOT trigger search
  const handleKeywordChange = (e) => {
    setKeyword(e.target.value);
  };

  // Triggers the search
  const handleSearchSubmit = () => {
    if (keyword.trim() !== search) {
      setSearch(keyword.trim());
      setPage(1);
    }
  };

  // Handle 'Enter' key in input
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleSearchSubmit();
    }
  };

  const handleClearSearch = () => {
    setKeyword("");
    setSearch("");
    setPage(1);
  };

  const handlePageChange = (event, value) => {
    setPage(value);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleTabChange = (newType) => {
    setSearch("");
    setKeyword("");
    setPage(1);
    navigate("/counseling/doctorChats", { state: { status: newType } });
  };

  const sessions = type === "active" ? activeSessions : historySessions;

  return (
    <>
      <style jsx>{`
        .doctor-chat-page {
          min-height: 100vh;
          background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
          font-family: "iran-sans", sans-serif;
          direction: rtl;
        }

        .page-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 24px;
        }

        .page-header {
          background: white;
          border-radius: 20px;
          padding: 32px;
          box-shadow: 0 4px 20px rgba(5, 50, 81, 0.08);
          border: 1px solid rgba(5, 50, 81, 0.05);
          margin-bottom: 32px;
        }

        .header-content {
          display: flex;
          align-items: center;
          gap: 16px;
        }

        .header-icon {
          width: 60px;
          height: 60px;
          background: linear-gradient(135deg, #050505ff 0%, #6ab77d 100%);
          border-radius: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 24px;
        }

        .header-text h1 {
          font-size: 28px;
          font-weight: 700;
          color: #053251;
          margin: 0 0 8px 0;
        }

        .header-text p {
          font-size: 16px;
          color: #6c757d;
          margin: 0;
        }

        .tabs-container {
          display: flex;
          gap: 12px;
          margin-bottom: 32px;
        }

        .tab-button {
          flex: 1;
          background: white;
          border: 2px solid rgba(5, 50, 81, 0.1);
          border-radius: 16px;
          padding: 16px 24px;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          font-size: 16px;
          font-weight: 600;
          color: #6c757d;
        }

        .tab-button:hover {
          border-color: rgba(124, 200, 144, 0.3);
          background: rgba(124, 200, 144, 0.05);
        }

        .tab-button.active {
          background: linear-gradient(135deg, #053251 0%, #074a6b 100%);
          border-color: #053251;
          color: white;
        }

        .consultations-card {
          background: white;
          border-radius: 20px;
          padding: 32px;
          box-shadow: 0 4px 20px rgba(5, 50, 81, 0.08);
          border: 1px solid rgba(5, 50, 81, 0.05);
        }

        .section-header {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 24px;
          padding-bottom: 16px;
          border-bottom: 2px solid rgba(124, 200, 144, 0.2);
        }
        .search-input {
          width: 100%;
          padding: 12px 16px 12px 40px;
          border: 2px solid rgba(5, 50, 81, 0.1);
          border-radius: 12px;
          font-size: 12px;
          font-family: "MyPersianFont", sans-serif !important;
          transition: all 0.3s ease;
          background: #f8f9fa;
          height: 48px;
        }

        .search-input::placeholder {
          color: #adb5bd;
        }

        .search-input::-webkit-input-placeholder {
          font-family: "MyPersianFont", sans-serif !important;
        }

        .search-input::-moz-placeholder {
          font-family: "MyPersianFont", sans-serif !important;
        }

        .search-input:-ms-input-placeholder {
          font-family: "MyPersianFont", sans-serif !important;
        }

        .search-icon {
          position: absolute;
          left: 14px;
          top: 50%;
          transform: translateY(-50%);
          color: #6c757d;
          pointer-events: none;
        }

        .section-icon {
          width: 40px;
          height: 40px;
          background: linear-gradient(
            135deg,
            rgba(124, 200, 144, 0.15) 0%,
            rgba(124, 200, 144, 0.08) 100%
          );
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #7cc890;
          font-size: 18px;
        }

        .section-title {
          font-size: 22px;
          font-weight: 700;
          color: #053251;
          margin: 0;
        }

        .results-count {
          font-size: 14px;
          color: #6c757d;
          background: rgba(124, 200, 144, 0.1);
          padding: 6px 14px;
          border-radius: 20px;
          font-weight: 600;
        }

        /* Modified Search Filter */
        .search-filter {
          margin-bottom: 20px;
          display: flex;
          gap: 12px;
          align-items: center;
        }

        .search-input-wrapper {
          flex: 1;
          position: relative;
        }

        .search-input {
          width: 100%;
          padding: 12px 16px 12px 40px; /* Adjusted padding */
          border: 2px solid rgba(5, 50, 81, 0.1);
          border-radius: 12px;
          font-size: 15px;
          font-family: "iran-sans", sans-serif;
          transition: all 0.3s ease;
          background: #f8f9fa;
          height: 48px;
        }

        .search-input:focus {
          outline: none;
          border-color: #7cc890;
          background: white;
        }

        .search-input::placeholder {
          color: #adb5bd;
        }

        /* New Search Button Style */
        .search-btn {
          height: 48px;
          padding: 0 24px;
          background: linear-gradient(135deg, #053251 0%, #074a6b 100%);
          color: white;
          border: none;
          border-radius: 12px;
          font-family: "iran-sans", sans-serif;
          font-weight: 600;
          font-size: 15px;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 8px;
          transition: all 0.3s ease;
          box-shadow: 0 4px 10px rgba(5, 50, 81, 0.15);
        }

        .search-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 15px rgba(5, 50, 81, 0.25);
        }

        .search-btn:active {
          transform: translateY(0);
        }

        .clear-search-btn {
          position: absolute;
          left: 12px;
          top: 50%;
          transform: translateY(-50%);
          background: none;
          border: none;
          color: #6c757d;
          cursor: pointer;
          padding: 4px 8px;
          transition: color 0.3s;
          display: flex;
          align-items: center;
        }

        .clear-search-btn:hover {
          color: #dc3545;
        }

        .consultations-list {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .pagination-wrapper {
          display: flex;
          justify-content: center;
          margin-top: 24px;
          padding-top: 24px;
          border-top: 1px solid rgba(5, 50, 81, 0.1);
        }

        .pagination-wrapper :global(.MuiPagination-root) {
          direction: ltr;
        }

        .pagination-wrapper :global(.MuiPaginationItem-root) {
          font-family: "iran-sans", sans-serif;
          font-weight: 600;
          color: #053251;
        }

        .pagination-wrapper :global(.MuiPaginationItem-root.Mui-selected) {
          background: linear-gradient(135deg, #7cc890 0%, #6ab77d 100%);
          color: white;
        }

        .pagination-wrapper :global(.MuiPaginationItem-root:hover) {
          background-color: rgba(124, 200, 144, 0.1);
        }

        .empty-state {
          text-align: center;
          padding: 60px 32px;
          color: #6c757d;
        }

        .empty-state-icon {
          width: 80px;
          height: 80px;
          background: linear-gradient(
            135deg,
            rgba(108, 117, 125, 0.1) 0%,
            rgba(108, 117, 125, 0.05) 100%
          );
          border-radius: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 20px;
          color: #adb5bd;
          font-size: 32px;
        }

        .empty-state h3 {
          font-size: 20px;
          font-weight: 600;
          color: #495057;
          margin-bottom: 12px;
        }

        .empty-state p {
          font-size: 16px;
          line-height: 1.6;
        }

        .list-loading-overlay {
          display: flex;
          justify-content: center;
          align-items: center;
          min-height: 200px;
          width: 100%;
        }

        /* فضای خالی برای نوبار پایین موبایل تا آخرین کارت زیر آن نرود */
        @media (max-width: 991.98px) {
          .page-container {
            padding-bottom: 120px;
          }
        }

        @media (max-width: 768px) {
          .page-container {
            padding: 16px 16px 120px;
          }
          .page-header {
            padding: 24px;
          }
          .header-content {
            flex-direction: column;
            text-align: center;
          }
          .header-text h1 {
            font-size: 24px;
          }
          .tabs-container {
            flex-direction: column;
          }
          .consultations-card {
            padding: 20px;
          }
          .section-header {
            flex-wrap: wrap;
          }
          .section-title {
            font-size: 20px;
          }
          .results-count {
            width: 100%;
            margin-top: 8px;
            text-align: center;
          }
          .search-filter {
            flex-direction: column;
            align-items: stretch;
          }
          .search-btn {
            justify-content: center;
          }
        }
      `}</style>

      {initialLoading ? (
        <Loader />
      ) : (
        <div className="doctor-chat-page iran-sans">
          <div className="page-container iran-sans">
            <div className="page-header">
              <div className="header-content">
                <div className="header-icon">
                  <FontAwesomeIcon icon={faUserMd} />
                </div>
                <div className="header-text">
                  <h1>مشاوره های پزشک</h1>
                  <p>مدیریت و پاسخگویی به درخواست‌های مشاوره بیماران</p>
                </div>
              </div>
            </div>

            <div className="tabs-container iran-sans">
              <button
                className={`tab-button ${type === "active" ? "active" : ""}`}
                onClick={() => handleTabChange("active")}
                disabled={listLoading}
              >
                <FontAwesomeIcon icon={faComments} />
                مشاوره‌های جاری
              </button>
              <button
                className={`tab-button ${type === "finished" ? "active" : ""}`}
                onClick={() => handleTabChange("finished")}
                disabled={listLoading}
              >
                <FontAwesomeIcon icon={faHistory} />
                مشاوره‌های تمام شده
              </button>
            </div>

            <div className="consultations-card">
              <div className="section-header">
                <div className="section-icon">
                  <FontAwesomeIcon
                    icon={type === "active" ? faCheckCircle : faArchive}
                  />
                </div>
                <h2 className="section-title">
                  {type === "active"
                    ? "مشاوره های فعال"
                    : "مشاوره های تمام شده"}
                </h2>
                {totalCount > 0 && (
                  <span className="results-count">{totalCount} مشاوره</span>
                )}
              </div>

              <div className="search-filter">
                <div className="search-input-wrapper">
                  <input
                    type="text"
                    className="search-input iran-sans"
                    placeholder="جستجو بر اساس نام، نام خانوادگی یا موبایل (بدون 0)..."
                    value={keyword}
                    onChange={handleKeywordChange}
                    onKeyDown={handleKeyDown}
                  />
                  {keyword && (
                    <button
                      className="clear-search-btn"
                      onClick={handleClearSearch}
                      title="پاک کردن جستجو"
                    >
                      <FontAwesomeIcon icon={faTimes} />
                    </button>
                  )}
                </div>
                <button className="search-btn" onClick={handleSearchSubmit}>
                  <FontAwesomeIcon icon={faSearch} />
                  <span>جستجو</span>
                </button>
              </div>

              {listLoading ? (
                <div className="list-loading-overlay">
                  <CircularProgress color="primary" />
                </div>
              ) : sessions.length > 0 ? (
                <>
                  <div className="consultations-list">
                    {sessions.map((session) => (
                      <ChatItem
                        key={session.id}
                        session={session}
                        handleJoinChat={handleJoinChat}
                        isFromUser={false}
                      />
                    ))}
                  </div>

                  {totalPages > 1 && (
                    <div className="pagination-wrapper">
                      <Pagination
                        count={totalPages}
                        page={page}
                        className="iran-sans"
                        classes={{ fontFamily: "MyPersianFont, sans-serif" }}
                        onChange={handlePageChange}
                        color="primary"
                        size="large"
                        showFirstButton
                        showLastButton
                      />
                    </div>
                  )}
                </>
              ) : (
                <div className="empty-state">
                  <div className="empty-state-icon">
                    <FontAwesomeIcon
                      icon={type === "active" ? faComments : faArchive}
                    />
                  </div>
                  <h3>
                    {search
                      ? "نتیجه‌ای یافت نشد"
                      : type === "active"
                      ? "هیچ مشاوره فعالی وجود ندارد"
                      : "هیچ مشاوره تمام شده‌ای یافت نشد"}
                  </h3>
                  <p>
                    {search
                      ? "لطفاً کلمات دیگری را امتحان کنید."
                      : type === "active"
                      ? "در حال حاضر هیچ درخواست مشاوره‌ای در انتظار پاسخگویی نیست."
                      : "تاریخچه مشاوره‌های تکمیل شده در اینجا نمایش داده می‌شود."}
                  </p>
                </div>
              )}
            </div>
          </div>

          <Notification
            openSnackbar={openSnackbar}
            snackbarMessage={snackbarMessage}
            snackbarSeverity={snackbarSeverity}
            setOpenSnackbar={setOpenSnackbar}
          />
        </div>
      )}
    </>
  );
};

export default DoctorChatIndex;
